import styled, { keyframes } from "styled-components";
import { Modal as BaseModal } from "@wrstudios/components";
import { default as BaseInput } from "../../../common/Input";
import { Select as BaseSelect } from "../../../common/Select";
import { default as BaseMapStatic } from "../../../common/MapStatic";

const teeter = keyframes`
  0% { transform: rotate(10deg); }
  50% { transform: rotate(-10deg); }
  100% { transform: rotate(0deg); }
`;

const slide = keyframes`
  0% { transform: translate(2%); }
  50% { transform: translate(-2%); }
  100% { transform: translate(2%); }
`;

export const Guide = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: auto;
  padding: 1.6rem;
`;

export const GuideIllustration = styled.div`
  margin-bottom: 2.4rem;

  .animated {
    transform-origin: center;
    animation: ${teeter} 2.5s forwards;
  }

  .shadow {
    transform-origin: center;
    animation: ${slide} 2.5s forwards;
  }

  svg {
    width: 14.8rem;
    height: 8.8rem;
  }
`;

export const GuideTitle = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 2.2rem;
  margin-bottom: 1.2rem;
  text-align: center;
  color: ${({ theme }) => theme.styleGuide.colors.gray[800]};

  ${({ theme }) => theme.media.sm`
    font-size: 1.7rem;
    line-height: 2.4rem;
  `}
`;

export const List = styled.ul`
  display: inline-block;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Item = styled.li`
  display: flex;
  align-items: baseline;
  font-size: 1.2rem;
  line-height: 1.6rem;
  margin-bottom: 0.4rem;
  color: ${({ theme }) => theme.styleGuide.colors.gray[800]};

  ${({ theme }) => theme.media.sm`
    font-size: 1.3rem;
    line-height: 1.7rem;
  `}

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    font-weight: 700;
  }
`;

export const Color = styled.div`
  flex-shrink: 0;
  display: inline-block;
  width: 0.8rem;
  height: 0.8rem;
  margin-right: 0.8rem;
  border-radius: 0.8rem;
  background-color: ${getStatusColor};
`;

export const MapStatic = styled(BaseMapStatic)`
  position: absolute;
`;

export const ModalContent = styled(BaseModal.Content)`
  max-width: 50rem;

  .address-auto-complete-input {
    height: 3.6rem;
    padding: 0.8rem 1.2rem;
    border-width: 0.1rem;

    &:active,
    &:focus {
      border-color: ${({ theme }) => theme.live.colors.primary};
    }
  }

  .address-auto-complete-menu {
    border-width: 0.1rem;
  }

  .is-highlighted {
    color: ${({ theme }) => theme.live.colors.primary};
  }
`;

export const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -0.8rem;
  overflow: hidden;
`;

export const Cell = styled.div`
  padding: 0.8rem;
`;

export const DetailsCell = styled(Cell)`
  width: 50%;

  ${({ theme }) => theme.media.sm`
    width: 100%;
  `}
`;

export const PhotoCell = styled(Cell)`
  display: flex;
  flex-direction: column;
  width: 50%;

  ${({ theme }) => theme.media.sm`
    width: 100%;
  `}
`;

export const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: -0.8rem;
`;

export const Action = styled.div`
  padding: 0.8rem;
`;

export const Input = styled(BaseInput)`
  height: 3.6rem;
  padding: 0.8rem 1.2rem;
  border-width: 0.1rem;
  color: ${({ theme, hasTaxData }) =>
    hasTaxData && theme.styleGuide.colors.purple[500]};

  &:active,
  &:focus {
    border-color: ${({ theme }) => theme.live.colors.primary};
  }
`;

export const Select = styled(BaseSelect)`
  height: 3.6rem;
  padding: 0.8rem 1.2rem;
  border-width: 0.1rem;

  &:active,
  &:focus {
    border-color: ${({ theme }) => theme.live.colors.primary};
  }
`;

export const Button = styled.button`
  display: flex;
  align-items: center;
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 2rem;
  padding: 0.8rem 1.6rem;
  border: 0;
  appearance: none;
  color: ${({ theme }) => theme.styleGuide.colors.white};
  background-color: ${({ theme }) => theme.live.colors.primary};
  box-shadow: ${({ theme }) => theme.styleGuide.shadows.raised};
  border-radius: 0.4rem;
  cursor: pointer;

  &[disabled] {
    opacity: 0.5;
  }
`;

export const Cancel = styled(Button)`
  padding: 0.7rem 1.5rem;
  box-shadow: none;
  color: ${({ theme }) => theme.live.colors.primary};
  background-color: transparent;
  border: 0.1rem solid ${({ theme }) => theme.live.colors.primary};
`;

export const Error = styled.p`
  font-size: 1.4rem;
  line-height: 2rem;
  margin: 0;
  color: ${({ theme }) => theme.styleGuide.colors.red[500]};

  ${({ theme }) => theme.media.sm`
    font-size: 1.5rem;
  `}
`;

function getStatusColor({ theme, status }) {
  switch (status) {
    case "active":
      return theme.styleGuide.colors.green[500];
    case "sold":
      return theme.styleGuide.colors.red[500];
    case "pending":
      return theme.styleGuide.colors.yellow[500];
    case "withdrawn":
      return theme.styleGuide.colors.blue[500];
    default:
      return theme.styleGuide.colors.gray[900];
  }
}
